import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { MutableRefObject, SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getFormattedPhoneNumber } from '../../..//utils/phone.utils';
import { useCoverageType } from '../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { FacilityLocationsResponse } from '../../../models/FacilityDetails';
import { ProviderLocation } from '../../../models/ProviderDetails';
import { getDistance } from '../../../utils/card.utils';
import { shouldUseGehaV2Styling } from '../../../utils/gehaThemeUtils';
import {
  getAcceptingNewPatientsStatus,
  shortenZipCode,
  showTier2ProviderTagOnHeader,
} from '../../../utils/providerDetails.utils';
import { capitalizeFirstLetters } from '../../../utils/providerNames.utils';
import { getCurrentMember } from '../../../utils/user.utils';
import { Constants, ReverseDentalCodes } from '../../Constants';
import { FeatureFlags } from '../../ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { phoneOnly } from '../../ConstantsStyles';
import { CallModal } from '../../DataCard/CallModal';
import {
  BenefitsBadge,
  BenefitsBadgeType,
} from '../../DetailsPageHeader/BenefitsBadge';
import { getTierProviderEligibility } from '../../Utils/memberUtils/memberUtils';
import {
  AcceptingNewPatientsBadge,
  AddressDetailsWrapper,
  CardContentRow,
  CheckboxWrapper,
  LocationBadgeRow,
  LocationCardContainer,
  LocationCardWrapper,
  LocationDetailsAddress,
  LocationDetailsStack,
  MilesButton,
  ModalPhoneText,
  PhoneLink,
  PhoneTag,
  PreferredBadgeWrapper,
  SelectLocationCheckbox,
} from './CareTeamSelectLocationCard.style';

export type Props = {
  className?: string;
  highlightRefs?: MutableRefObject<any>;
  handleDirectionClick: () => void;
  index?: number;
  isDisabled?: boolean;
  location: ProviderLocation | FacilityLocationsResponse;
  selectLocation: () => void;
  selected?: boolean;
  showBadgeRow?: boolean;
  showRadioSelect?: boolean;
  totalLocations?: number;
  isFromCareTeam?: boolean;
};

export const CareTeamSelectLocationCard = ({
  location,
  showBadgeRow,
  selected,
  selectLocation,
  handleDirectionClick,
  className = '',
  index = 0,
  totalLocations = 0,
  isDisabled = false,
  isFromCareTeam = false,
}: Props) => {
  const phoneScreen = useMediaQuery(phoneOnly);
  const { t } = useTranslation();
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const {
    acceptingNewPatients,
    address,
    distance,
    isGreenDiamondProvider,
    isTieredProvider,
    isTier2Provider,
    phones,
    providerGroupsAffiliations,
  } = location;
  const { city, line, state, postalCode, locationName } = address;
  const { phone } = phones ?? {};

  const addressFontSizeMobile = phoneScreen ? 'sm' : 'lg';

  const coverageType = useCoverageType();

  const dentalCoverage = ReverseDentalCodes.includes(coverageType);

  const currentMember = getCurrentMember();
  const isCns = currentMember?.isCnSMember;

  const [includeProviderGroupName] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.INCLUDE_PROVIDER_GROUP_NAME,
  ]);

  const [showTier1Badge] = useFeatureFlag([
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[
      Constants.CHIPS_CATEGORIES.PRIMARY_CARE
    ].TIER_ONE,
  ]);

  const [showTier2Badge] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_TWO_PROVIDER,
  ]);
  const [isDentalLocationNameEnable] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_DENTAL_LOCATION_NAME,
  ]);
  const displayCityOrState = [city?.trim(), state?.trim()]
    .filter(Boolean)
    .join(', ')
    .trim();
  const displayLocationName =
    dentalCoverage && isDentalLocationNameEnable ? locationName : '';
  const lineAddressString = line?.filter((l) => l?.trim()).join(', ');
  const addressString = [displayCityOrState, shortenZipCode(postalCode)]
    .filter(Boolean)
    .join(' ');
  const locationNumberText = t('CHOOSE_PCP_FLOW.LOCATION_NUMBER', {
    index: index + 1,
    total: totalLocations,
  });
  const providerGroupName = providerGroupsAffiliations?.[0]?.value
    ? capitalizeFirstLetters(providerGroupsAffiliations[0].value)
    : '';
  const checkboxLabel = [
    locationNumberText,
    includeProviderGroupName ? providerGroupName : '',
    displayLocationName,
    lineAddressString,
    addressString,
  ]
    .filter(Boolean)
    .join(', ');

  // Clinic display name used in ARIA label: prefer provider group name, fall back to location name
  const clinicDisplayName =
    (includeProviderGroupName && providerGroupName) ||
    displayLocationName ||
    '';

  // ARIA label follows the pattern: "Location [X of Y], [Clinic name], Select [Clinic name]"
  const checkboxAriaLabel = [
    `Location ${locationNumberText}`,
    clinicDisplayName,
    clinicDisplayName
      ? `Select ${clinicDisplayName}`
      : t('CHOOSE_PCP_FLOW.CHOOSE_THIS_LOCATION'),
  ]
    .filter(Boolean)
    .join(', ');

  const displayTier1Badge =
    showTier1Badge &&
    getTierProviderEligibility(currentMember, isTieredProvider);

  const displayTier2Badge = showTier2ProviderTagOnHeader(
    showTier2Badge,
    currentMember?.isIFPTieredPlan,
    isTier2Provider
  );

  const { icon, iconColor, acceptingPatientInfo } =
    getAcceptingNewPatientsStatus(acceptingNewPatients);

  const isGehaTheme = shouldUseGehaV2Styling();

  const phonePrimaryTestId = 'phone-primary-detailes-header';
  return (
    <LocationCardContainer
      data-testid="select-location-container"
      onClick={() => {
        handleDirectionClick();
      }}
    >
      <CardContentRow>
        <LocationDetailsStack
          alignItems="flex-start"
          className="location-details"
          data-testid="location-details-stack"
        >
          <div>
            <Text
              color="$core.color.neutral.90"
              fontSize={phoneScreen ? 'sm' : 'md'}
              fontWeight="medium"
            >
              {locationNumberText}
            </Text>
          </div>
          <LocationCardWrapper
            className={`location-card-wrap ${className}`}
            data-auto-testid="select-location-card"
            data-testid="select-location-card"
          >
            {isGreenDiamondProvider && !isCns ? (
              <PreferredBadgeWrapper>
                <BenefitsBadge
                  badgeType={BenefitsBadgeType.GREEN_DIAMOND}
                  showInfoIcon={false}
                />
              </PreferredBadgeWrapper>
            ) : null}
            {!isGreenDiamondProvider && !isCns && displayTier1Badge ? (
              <PreferredBadgeWrapper>
                <BenefitsBadge
                  badgeType={BenefitsBadgeType.TIER_1}
                  showInfoIcon={false}
                />
              </PreferredBadgeWrapper>
            ) : null}
            {!isGreenDiamondProvider &&
            !displayTier1Badge &&
            displayTier2Badge ? (
              <PreferredBadgeWrapper>
                <BenefitsBadge
                  badgeType={BenefitsBadgeType.TIER_2}
                  showInfoIcon={false}
                />
              </PreferredBadgeWrapper>
            ) : null}
            {includeProviderGroupName && providerGroupName && (
              <LocationDetailsAddress
                color={isGehaTheme ? '$Primary1' : '$core.color.neutral.90'}
                data-testid="location-provider-group"
                fontSize={phoneScreen ? 'md' : 'lg'}
                fontWeight="bold"
              >
                {providerGroupName}
              </LocationDetailsAddress>
            )}
            <div>
              {displayLocationName && (
                <Text
                  color="black"
                  data-testid="display-location-name"
                  fontSize={addressFontSizeMobile}
                  fontWeight="medium"
                >
                  {displayLocationName}
                </Text>
              )}
              {(lineAddressString || addressString) && (
                <AddressDetailsWrapper>
                  {lineAddressString && (
                    <Text
                      color={
                        isGehaTheme ? '$Primary1' : '$core.color.neutral.90'
                      }
                      data-testid="address-line-string"
                      fontSize={addressFontSizeMobile}
                      fontWeight="medium"
                    >
                      {lineAddressString}
                    </Text>
                  )}
                  {addressString && (
                    <Text
                      color={
                        isGehaTheme ? '$Primary1' : '$core.color.neutral.90'
                      }
                      data-testid="address-city-state-string"
                      fontSize={addressFontSizeMobile}
                      fontWeight="medium"
                    >
                      {addressString}
                    </Text>
                  )}
                </AddressDetailsWrapper>
              )}
            </div>
          </LocationCardWrapper>
        </LocationDetailsStack>
        {/* Prevent checkbox click from bubbling to the card's handleDirectionClick */}
        <CheckboxWrapper
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          <SelectLocationCheckbox
            aria-label={checkboxAriaLabel}
            data-testid="choose-location-checkbox"
            hideLabel
            isChecked={selected}
            isDisabled={isDisabled}
            label={checkboxLabel}
            onChange={selectLocation}
          />
        </CheckboxWrapper>
      </CardContentRow>
      {showBadgeRow ? (
        <LocationBadgeRow
          data-auto-testid="select-location-badge-row"
          data-testid="select-location-badge-row"
        >
          <MilesButton
            data-auto-testid="select-location-miles-button"
            data-testid="select-location-miles-button"
          >
            <IconSymbol
              color="$core.color.brand.100"
              icon="directions"
              size="20"
            />
            <Text
              color="$core.color.neutral.90"
              fontSize={phoneScreen ? 'sm' : 'md'}
              fontWeight="medium"
            >
              {getDistance(distance)} {t('miles')}
            </Text>
          </MilesButton>
          <AcceptingNewPatientsBadge>
            <IconSymbol color={iconColor} icon={icon as any} size="$sm" />
            <Text
              color={isGehaTheme ? '$Primary1' : '$core.color.neutral.90'}
              fontSize={phoneScreen ? 'sm' : 'md'}
              fontWeight="medium"
            >
              {t(acceptingPatientInfo)}
            </Text>
          </AcceptingNewPatientsBadge>
        </LocationBadgeRow>
      ) : null}
      {isFromCareTeam && phone?.[0] && (
        <>
          <PhoneLink
            data-testid="update-location-phone-number"
            href={`tel:+1-${phone[0]}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsCallModalOpen(true);
            }}
          >
            <IconSymbol icon="phone" size="sm" />
            {phone[0]}
          </PhoneLink>
          {isCallModalOpen && (
            <CallModal
              isOpen={isCallModalOpen}
              setIsOpen={setIsCallModalOpen}
              title="Provider Detail Call Modal"
            >
              <PhoneTag
                data-auto-testid={phonePrimaryTestId}
                data-testid="call-modal-phone-number"
                href={`tel:${getFormattedPhoneNumber(phone[0])}`}
                onClick={(e: SyntheticEvent) => e.stopPropagation()}
              >
                <IconSymbol
                  data-auto-testid={`data-card-phone-icon-${phonePrimaryTestId}`}
                  data-testid={`data-card-phone-icon-${phonePrimaryTestId}`}
                  icon="phone"
                  size={phoneScreen ? 18 : 24}
                />
                <ModalPhoneText
                  color="$core.color.brand.60"
                  data-auto-testid={`data-card-phone-number-${phonePrimaryTestId}`}
                  data-testid={`data-card-phone-number-${phonePrimaryTestId}`}
                  fontWeight="bold"
                >
                  {t(`${getFormattedPhoneNumber(phone[0])}`)}
                </ModalPhoneText>
              </PhoneTag>
            </CallModal>
          )}
        </>
      )}
    </LocationCardContainer>
  );
};
